/**
 * Schemas para membresia tab — snapshot trimestral agregado.
 *
 * Per FWA-WORKER-029 G0 + data-model V1.1 §3.10.
 *
 * Datos agregados sin PII individual (counts por rama/estatus + altas/bajas).
 * Per privacy.md §6.1: counts agregados NO son PII — safe en summary + audit.
 */
import { z } from 'zod';
import { QuarterSchema, VersionedFieldsSchema } from './common.js';

// ─── Counts records ────────────────────────────────────────────────────────

export const PorRamaCountsSchema = z.record(z.string(), z.number().int().min(0));
export type PorRamaCounts = z.infer<typeof PorRamaCountsSchema>;

export const PorEstatusCountsSchema = z.record(z.string(), z.number().int().min(0));
export type PorEstatusCounts = z.infer<typeof PorEstatusCountsSchema>;

// ─── Master schema ─────────────────────────────────────────────────────────

export const MembresiaSchema = VersionedFieldsSchema.extend({
  quarter: QuarterSchema,
  total_ninas: z.number().int().min(0),
  total_activas: z.number().int().min(0),
  total_bajas_trimestre: z.number().int().min(0),
  total_altas_trimestre: z.number().int().min(0),
  por_rama_json: PorRamaCountsSchema,
  por_estatus_json: PorEstatusCountsSchema,
});
export type Membresia = z.infer<typeof MembresiaSchema>;

// ─── Backfill endpoint payload ─────────────────────────────────────────────

export const MembresiaBackfillParamsSchema = z.object({
  quarter: QuarterSchema,
});
export type MembresiaBackfillParams = z.infer<typeof MembresiaBackfillParamsSchema>;

// ─── Quarter window helper (server-side aggregation) ───────────────────────

/**
 * Quarter window inclusive [start, end] en formato ISO date.
 *
 * Aplica a aggregateMembresia: filter altas/bajas por fecha dentro de window.
 *
 * Examples:
 *   '2026-Q1' → { start: '2026-01-01', end: '2026-03-31' }
 *   '2026-Q4' → { start: '2026-10-01', end: '2026-12-31' }
 */
export function quarterToDateRange(quarter: string): { start: string; end: string } {
  const m = quarter.match(/^(\d{4})-Q([1-4])$/);
  if (!m) throw new Error(`invalid quarter format: ${quarter}`);
  const year = Number.parseInt(m[1] ?? '0', 10);
  const q = Number.parseInt(m[2] ?? '0', 10);
  const startMonth = (q - 1) * 3 + 1; // Q1→1, Q2→4, Q3→7, Q4→10
  const endMonth = startMonth + 2;
  const lastDay = new Date(Date.UTC(year, endMonth, 0)).getUTCDate(); // último día del mes
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    start: `${year}-${pad(startMonth)}-01`,
    end: `${year}-${pad(endMonth)}-${pad(lastDay)}`,
  };
}

export function isDateInQuarter(isoDate: string | null | undefined, quarter: string): boolean {
  if (!isoDate) return false;
  const { start, end } = quarterToDateRange(quarter);
  // ISO date strings se pueden comparar lexicográficamente cuando son fechas YYYY-MM-DD
  const onlyDate = isoDate.length >= 10 ? isoDate.slice(0, 10) : isoDate;
  return onlyDate >= start && onlyDate <= end;
}
